import { useQuery, useMutation } from "@tanstack/react-query";
import { useWorkspaceStore } from "./use-workspace";
import { queryClient, apiRequest } from "@/lib/queryClient";
import type { AgentLog } from "@shared/schema";
import { useState, useEffect, useRef, useCallback } from "react";

export interface StreamAgentQuestion {
  id: number;
  streamKey: string;
  agent: string;
  question: string;
  context?: string;
  requiresApproval: boolean;
  isNudge?: boolean;
  createdAt: string;
}

export function useAgentLogs() {
  const { activeWorkspaceId } = useWorkspaceStore();
  return useQuery<AgentLog[]>({
    queryKey: ["/api/workspaces", activeWorkspaceId, "logs"],
    queryFn: async () => {
      if (!activeWorkspaceId) return [];
      const res = await fetch(`/api/workspaces/${activeWorkspaceId}/logs`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch logs");
      return res.json();
    },
    enabled: !!activeWorkspaceId,
  });
}

export function useAgentStream() {
  const { activeWorkspaceId } = useWorkspaceStore();
  const [streamLogs, setStreamLogs] = useState<AgentLog[]>([]);
  const [streamQuestions, setStreamQuestions] = useState<StreamAgentQuestion[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const eventSourceRef = useRef<EventSource | null>(null);

  useEffect(() => {
    if (!activeWorkspaceId) return;

    const es = new EventSource(`/api/workspaces/${activeWorkspaceId}/stream`, {
      withCredentials: true,
    } as any);
    eventSourceRef.current = es;

    es.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === "connected") {
          setIsConnected(true);
          return;
        }
        if (data.type === "agent_question") {
          const isNudge = !!data.isNudge;
          const streamKey = isNudge ? `${data.questionId}:nudge:${Date.now()}` : `${data.questionId}`;
          const sq: StreamAgentQuestion = {
            id: data.questionId,
            streamKey,
            agent: data.agent,
            question: data.question,
            context: data.context,
            requiresApproval: !!data.requiresApproval,
            isNudge,
            createdAt: new Date().toISOString(),
          };
          setStreamQuestions((prev) => {
            // For regular questions: dedupe by id (don't add twice)
            if (!isNudge && prev.some((q) => q.streamKey === streamKey)) return prev;
            return [...prev, sq];
          });
          queryClient.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "agent-questions"] });
          return;
        }
        if (data.type === "agent_question_answered" || data.type === "agent_question_dismissed") {
          setStreamQuestions((prev) => prev.filter((q) => q.id !== data.questionId));
          queryClient.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "agent-questions"] });
          return;
        }
        setStreamLogs((prev) => [...prev, data]);
      } catch {}
    };

    es.onerror = () => {
      setIsConnected(false);
    };

    return () => {
      es.close();
      eventSourceRef.current = null;
    };
  }, [activeWorkspaceId]);

  const clearStream = useCallback(() => {
    setStreamLogs([]);
    setStreamQuestions([]);
  }, []);

  return { streamLogs, streamQuestions, isConnected, clearStream };
}

export function useOrchestrate() {
  const { activeWorkspaceId } = useWorkspaceStore();

  return useMutation({
    mutationFn: async ({ url, businessName }: { url: string; businessName: string }) => {
      const res = await apiRequest("POST", `/api/workspaces/${activeWorkspaceId}/orchestrate`, {
        url,
        businessName,
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "plans"] });
      queryClient.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "content"] });
      queryClient.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "campaigns"] });
      queryClient.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "logs"] });
    },
  });
}
